import React from 'react';
import { Alert, Button, Empty } from 'antd';
import { AxiosError } from 'axios';
import { useIntl } from 'react-intl';
import SpinnerChidori from 'components/SpinnerChidori/SpinnerChidori';
import TimeSeriesChart from 'components/MetricsLayout/TimeSeriesChart/TimeSeriesChart';
import {
  formatPercent,
  reduceAverage,
  reduceMax,
  reduceP95,
} from 'utils/insights';
import './UtilizationCard.scss';

export type UtilizationSeries = {
  name: string;
  data: number[];
};

type UtilizationCardProps = {
  title: string;
  subtitle: string;
  series: UtilizationSeries[];
  timeData: string[];
  loading: boolean;
  error: unknown;
  onRetry: () => void;
};

function getErrorStatus(error: unknown): number | undefined {
  if (!error) return undefined;
  const axErr = error as AxiosError;
  return axErr?.response?.status;
}

function UtilizationCard({
  title,
  subtitle,
  series,
  timeData,
  loading,
  error,
  onRetry,
}: UtilizationCardProps) {
  const intl = useIntl();
  const status = getErrorStatus(error);

  const allValues = series.flatMap((s) => s.data);
  const hasData =
    timeData.length > 0 && series.some((s) => s.data.length > 0);

  const maxVal = reduceMax(allValues);
  const avgVal = reduceAverage(allValues);
  const p95Val = reduceP95(allValues);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="overview-card__body-state">
          <SpinnerChidori />
        </div>
      );
    }

    if (error) {
      let messageId = 'overview.errors.upstream';
      let alertType: 'error' | 'warning' = 'error';
      if (status === 503) {
        messageId = 'overview.errors.unavailable';
        alertType = 'warning';
      } else if (status === 400) {
        messageId = 'overview.errors.invalidParams';
      }

      return (
        <div className="overview-card__body-state">
          <Alert
            type={alertType}
            showIcon
            message={intl.formatMessage({ id: messageId })}
            description={
              <div className="overview-card__body-state-actions">
                <Button onClick={onRetry} size="small">
                  {intl.formatMessage({ id: 'overview.retry' })}
                </Button>
              </div>
            }
          />
        </div>
      );
    }

    if (!hasData) {
      return (
        <div className="overview-card__body-state">
          <Empty
            description={intl.formatMessage({ id: 'overview.empty' })}
          />
        </div>
      );
    }

    return (
      <TimeSeriesChart
        chartType="smooth"
        chartTitle=""
        yAxisFormatter={(val: number) => `${val.toFixed(1)} %`}
        yAxisTitle={intl.formatMessage({ id: 'overview.yAxis.utilization' })}
        toolTipFormatter={(val: number) => `${val.toFixed(2)} %`}
        seriesData={series}
        timeData={timeData}
      />
    );
  };

  return (
    <article className="overview-card">
      <header className="overview-card__header">
        <div>
          <h3 className="overview-card__title">{title}</h3>
          <p className="overview-card__subtitle">{subtitle}</p>
        </div>
        {hasData && !loading && !error && (
          <ul className="overview-card__kpi-strip">
            <li className="overview-card__kpi">
              <span className="overview-card__kpi-label">
                {intl.formatMessage({ id: 'overview.kpi.max' })}
              </span>
              <span className="overview-card__kpi-value">
                {formatPercent(maxVal, 1)}
              </span>
            </li>
            <li className="overview-card__kpi">
              <span className="overview-card__kpi-label">
                {intl.formatMessage({ id: 'overview.kpi.avg' })}
              </span>
              <span className="overview-card__kpi-value">
                {formatPercent(avgVal, 1)}
              </span>
            </li>
            <li className="overview-card__kpi">
              <span className="overview-card__kpi-label">
                {intl.formatMessage({ id: 'overview.kpi.p95' })}
              </span>
              <span className="overview-card__kpi-value">
                {formatPercent(p95Val, 1)}
              </span>
            </li>
          </ul>
        )}
      </header>
      <div className="overview-card__body">{renderBody()}</div>
    </article>
  );
}

export default UtilizationCard;
